export const overviewArticle = {
    id: 'overview',
    title: 'О системе IChis SPD',
    category: 'Основы',
    requiredModule: null,
    summary: 'Интерактивная электронная карта территории: назначение, возможности, модули и быстрые клавиши.',
    markdown: `
> **IChis SPD** — это интерактивная электронная карта вашей территории. Она открывается в обычном браузере на компьютере или планшете и помогает вести учёт участков, дорог и зданий без бумажных схем и путаницы в таблицах.

---

## 1. Зачем нужна система

Раньше, чтобы ответить на вопрос «Свободен ли участок №15 и сколько в нём соток?», приходилось открывать старый PDF-файл, сверять его с таблицей Excel и гадать, последняя ли это версия схемы. 

В IChis SPD вся информация живёт на одной общей карте.

| Как было раньше | Как работает в IChis SPD |
| :--- | :--- |
| **Много разных файлов:** схема на стене, таблица в Excel, выписки в папках. Информация теряется и устаревает. | **Всё в одном месте:** карта и список участков объединены. Изменили статус — все сразу видят обновлённые данные. |
| **Сложно искать:** чтобы узнать владельца или статус участка, нужно сопоставлять файлы вручную. | **Всё в один клик:** нажали на участок — на экране сразу появилась карточка с площадью, ценой и статусом. |
| **Сложные программы:** раньше требовалось ставить тяжелые инженерные программы, в которых тяжело разобраться. | **Простота Яндекс.Карт:** система работает через обычный браузер (Яндекс, Chrome). Ничего устанавливать не нужно. |

---

## 2. Основные возможности

:::grid cols=2
:::card icon="cursor" title="Вся информация по одному клику"
Кликните на любой участок или здание на карте. Справа откроется карточка объекта: его номер, точная площадь в сотках или метрах, статус (свободен или продан), адрес и примечания.
:::

:::card icon="palette" title="Понятные цвета вместо таблиц"
Статусы участков видны с первого взгляда благодаря цветовой подсветке: например, зелёные — свободны, жёлтые — на брони, синие — проданы. В любой момент можно включить спутниковый снимок, чтобы увидеть реальную местность.
:::

:::card icon="ruler" title="Быстрые замеры без линейки"
Нужно узнать длину забора, ширину дороги или площадь участка нестандартной формы? Встроенная электронная линейка измерит расстояния и площади по карте за пару секунд с точностью до сантиметра.
:::

:::card icon="display" title="Удобно показывать на встречах"
Карту можно открыть на большом экране на совещании или на планшете во время показа территории покупателю или инвестору. Всё выглядит аккуратно, наглядно и понятно без лишних бумаг.
:::
:::

---

## 3. Дополнительные модули

Базовая версия системы уже умеет вести учёт и рисовать карту. Если вам нужны специализированные задачи, к ней подключаются готовые модули:

| Модуль | Для чего он нужен |
| :--- | :--- |
| **Росреестр (НСПД)** | Больше не нужно перебивать выписки вручную. Официальные границы участков, кадастровые номера и площади подтягиваются из Росреестра по нажатию одной кнопки. |
| **Печать на бумаге** | Формирует красивый планшет карты для печати на бумаге любого формата (PDF или картинка) — со стрелкой на север, рамкой, масштабом и пояснениями. |
| **Умное редактирование (SPD+)** | Помогает быстро объединить несколько соседних участков в один общий, вырезать контур дома из земли или изменить статус сразу у группы объектов. |
| **Видеооблёт (Cinema 3D)** | Создает плавный 3D-видеоролик с облётом территории с высоты птичьего полета для презентаций или рекламы. |

---

## 4. Разделение доступа к карте

В системе предусмотрено два уровня доступа, чтобы гости могли свободно просматривать витрину, а конфиденциальные данные компании оставались под защитой:

:::grid cols=2
:::card icon="view" title="Публичный режим (для клиентов и гостей)"
Открыт для всех и работает только на просмотр. Здесь можно изучать карту, искать участки через фильтры и смотреть базовые параметры (номер, площадь, статус — свободен или продан). Служебные данные здесь скрыты, а случайно испортить или удалить что-то на карте гости не могут.
:::

:::card icon="settings" title="Административный режим (для сотрудников)"
Полноценный рабочий кабинет с входом по паролю. Здесь чертят новые границы, меняют статусы и обновляют описание объектов. Только здесь видны скрытые данные: контакты клиентов, внутренние заметки, прикреплённые документы и финансовые параметры.
:::
:::

:::info Навигация по руководству
В левом меню собраны подробные пошаговые инструкции по каждому инструменту и режиму системы. Если вам нужно подробнее узнать про конкретную функцию — просто выберите нужную главу в оглавлении слева.
:::

---

## 5. Полезные возможности для комфортной работы

:::grid cols=3
:::card icon="edit" title="Безопасная песочница"
В статьях документации про рисование прямо на странице встроено интерактивное окно «Песочница». Тренируйтесь кликать и проводить линии — данные реального проекта нельзя случайно испортить.
:::

:::card icon="display" title="Горячие клавиши на карте"
Клавиатурные комбинации в разы ускоряют черчение границ, помогают держать прямые углы и отменять ошибки. Подробнее читайте в разделе «Справочник → Горячие клавиши».
:::

:::card icon="layers" title="Быстрый поиск по справке"
Нажмите **Ctrl + K** или клавишу **/** (слэш) в любой момент, чтобы мгновенно найти нужную статью, подсказку по инструменту или спецификацию данных.
:::
:::
`
};
